import React from "react";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import noImage from "../../images/no-image-placeholder.png";
import {
  thousandSeperatedValue,
  removeDecimalZeros,
} from "../../helpers/common";

const STATUS_LABEL = {
  Active: "Active",
  ActiveUnderContract: "Under Contract",
  Pending: "Pending",
  Closed: "Closed",
  Sold: "Sold",
  Expired: "Expired",
};

const STATUS_VARIANT = {
  Active: "status-pill--active",
  ActiveUnderContract: "status-pill--contract",
  Pending: "status-pill--pending",
};

const formatPrice = (p) => {
  if (p === null || p === undefined || p === "") return null;
  const n = parseFloat(p);
  if (Number.isNaN(n) || n <= 0) return null;
  return "$" + thousandSeperatedValue(removeDecimalZeros(n));
};

const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return d;
  }
};

const MyMlsListingsRow = ({ row, onSeeMatches }) => {
  const navigate = useNavigate();
  const listingKey = _.get(row, "ListingKey");
  const photo = _.get(row, "photo");
  const status = _.get(row, "StandardStatus", "");
  const beds = _.get(row, "Bedrooms");
  const baths = _.get(row, "Bathrooms");
  const sqft = _.get(row, "Sqft");
  const totalAcre = _.get(row, "TotalAcre");
  const acreSqft = _.get(row, "Acre_Sqft");
  const price = formatPrice(_.get(row, "AskingPrice_Sell"));
  const potentialBuyers = _.get(row, "potentialBuyers", 0);
  const listingId = _.get(row, "ListingId");
  const published = _.get(row, "PublishedOn");

  const statusLabel = STATUS_LABEL[status] || status;
  const statusVariant = STATUS_VARIANT[status] || "";

  const addressLine1 = _.get(row, "Location") || "";
  const addressLine2 = [
    [_.get(row, "City"), _.get(row, "State")].filter(Boolean).join(", "),
    _.get(row, "ZIP"),
  ]
    .filter(Boolean)
    .join(" ");

  const handleRowClick = () => {
    if (!listingKey) return;
    navigate(`/mls-listing/${encodeURIComponent(listingKey)}`);
  };

  const handleSeeMatchesClick = (e) => {
    e.stopPropagation();
    if (onSeeMatches) return onSeeMatches(listingKey, row);
    if (!listingKey) return;
    navigate(`/mls-listing/${encodeURIComponent(listingKey)}/matches`, {
      state: {
        address: addressLine1,
        cityStateZip: addressLine2,
      },
    });
  };

  return (
    <div
      className="my-mls-row"
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleRowClick();
        }
      }}
    >
      <div className="my-mls-row__media">
        <img
          src={photo || noImage}
          alt=""
          onError={(e) => {
            e.currentTarget.src = noImage;
          }}
        />
        {statusLabel && (
          <span className={`status-pill ${statusVariant}`.trim()}>
            {statusLabel}
          </span>
        )}
      </div>

      <div className="my-mls-row__body">
        {price && <div className="my-mls-row__price">{price}</div>}

        <div className="my-mls-row__address">
          <div>{addressLine1}</div>
          <div>{addressLine2}</div>
        </div>

        <div className="my-mls-row__facts">
          {beds != null && <span>{beds} bd</span>}
          {baths != null && <span>{baths} ba</span>}
          {sqft != null && <span>{thousandSeperatedValue(sqft)} sqft</span>}
          {totalAcre && <span>{parseFloat(totalAcre).toFixed(2)} ac</span>}
        </div>

        <div className="my-mls-row__meta">
          {listingId && <span>MLS #{listingId}</span>}
          {published && <span>Listed {formatDate(published)}</span>}
        </div>
      </div>

      {potentialBuyers > 0 && (
        <div className="my-mls-row__actions">
          <button
            type="button"
            className="btn btn-outline-primary my-mls-row__cta"
            onClick={handleSeeMatchesClick}
            disabled={!listingKey}
          >
            See matches ({potentialBuyers})
          </button>
        </div>
      )}
    </div>
  );
};

export default MyMlsListingsRow;
